// ============================================
// USUARIOS.JS — Gestión de usuarios (Activos / Inactivos / Archivados)
// Conectado a backend real: /api/usuarios
// ============================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// PERM-1: catalogo unico de roles (espejo del backend)
import { ROLES_CREABLES, ROLES, ROLES_LISTA, ROLES_LABELS } from './utils/roles';
// PERM-1.5: useUsuario para identificar al admin actual (proteccion auto-rol)
import { useUsuario } from './context/UsuarioContext';
import { iniciarVerComo } from './utils/verComo';
// UX-USUARIOS: iconos Lucide (consistentes en todo dispositivo, adios emojis)
import {
  Shield, Eye, Pencil, KeyRound, Pause, Play, Archive, MoreVertical,
} from 'lucide-react';
import { API_BASE } from './config/api';
// AREAS-EN-USUARIOS (2026-09-24): de las areas sale a quien se le asignan los
// expedientes, y hasta hoy solo se podian tocar reiniciando el servidor.
import AreasDelUsuario from './components/AreasDelUsuario';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#1D9E75';
const COLOR_AMARILLO = '#dcde74';
const COLOR_ROJO = '#e05c5c';

// PERM-1: solo paleta visual (label y lista vienen del catalogo unico utils/roles.js)
const ROLES_VISUAL = {
  [ROLES.DIRECCION]:      { color: '#2f5f9e' },
  [ROLES.ADMINISTRACION]: { color: '#5c9adb' },
  [ROLES.GERENTE]:        { color: '#5cd4b0' },
  [ROLES.MARKETING]:      { color: '#dcde74' },
  [ROLES.OPERACIONES]:    { color: '#633806' },
  [ROLES.TECNICO]:        { color: '#9b59b6' },
  [ROLES.ALMACEN]:        { color: '#e05c5c' },
  [ROLES.VIEWER]:         { color: '#9aa0a6' },
  [ROLES.COORDINADOR]:    { color: '#1E7A4C' },
  [ROLES.CONTRATISTA_EXTERNO]: { color: '#8a5a00' },
};

// Estructura completa de módulos del sistema (espejo de Dashboard.js)
const MODULOS = [
  { id: 'mitrabajo',     icono: '📓', label: 'Mi Trabajo',     subs: [
    { id: 'midia',                 label: 'Mi día' },
    { id: 'agenda',                label: 'Mi agenda' },
    { id: 'tareas',                label: 'Mis tareas' },
    { id: 'gastos',                label: 'Mis gastos' },
    { id: 'agentes',               label: 'Mis agentes' },
    { id: 'vertodos',              label: 'Ver todos (supervisión)' },
  ]},
  { id: 'operaciones',   icono: '📋', label: 'Expedientes',    subs: [
    { id: 'expedientes-todos',     label: 'Todos los expedientes' },
    // EXPEDIENTES-RESUMEN (2026-09-17). Dos candados de DINERO, administrables desde aqui en vez de
    // estar cableados al nombre del rol (politica de permisos de CLAUDE.md). Hoy no los tiene
    // nadie, asi que de facto solo pasa direccion (que pasa siempre, por accesoTotal).
    { id: 'resumen-empresa',       label: 'Resumen de la empresa (cifras globales)' },
    { id: 'montos-todos',          label: 'Ver montos de expedientes ajenos' },
    // CIERRE-CON-PERMISO (2026-09-28). Cerrar como ganado, administrable desde aqui en vez de
    // estar cableado al nombre del rol. Vale para el boton Y para el cierre automatico al
    // registrar la factura: los dos caminos leen esta misma palomita.
    { id: 'cerrar-ganado',         label: 'Cerrar expedientes como ganados' },
    { id: 'calendario',            label: 'Calendario' },
    { id: 'contratistas',          label: 'Contratistas externos' },
    { id: 'servicios',             label: 'Servicios del día' },
    { id: 'mantenimientos',        label: 'Mantenimientos' },
    { id: 'programar',             label: 'Programar servicio' },
    { id: 'nueva-instalacion',     label: 'Nueva instalación' },
    { id: 'levantamientos',        label: 'Levantamientos' },
  ]},
  // TRANSPARENCIA-TOTAL (2026-07-09): "Control de Expedientes" se fusiono dentro de
  // Expedientes (abierto a todos). El modulo suelto y su permiso se eliminaron.
  // SUB-ALMACENES (2026-10-19): 4 pestanas con contenido propio (sincronizado con utils/permisos.js).
  { id: 'almacen',       icono: '📦', label: 'Almacén',        subs: [
    { id: 'inventario',            label: 'Central (bodega e inventario)' },
    { id: 'oficina',               label: 'Oficina (consumibles)' },
    { id: 'tecnicos',              label: 'Resguardos (material y herramientas a resguardo de cada persona)' },
    { id: 'equiposinstalados',     label: 'Instalados (series en sucursal)' },
    { id: 'compras',               label: 'Compras (sección Compras y ver costos)' },
  ]},
  { id: 'administracion', icono: '👥', label: 'Administración', subs: [
    { id: 'rrhh',                  label: 'Recursos Humanos' },
    { id: 'finanzas',              label: 'Finanzas' },
    { id: 'usuarios',              label: 'Usuarios' },
    { id: 'auditor',               label: 'Auditor del almacén (solo dirección)' },
    { id: 'configuracion',         label: 'Configuración' },
    { id: 'registro-accesos',      label: 'Registro de accesos (seguridad)' },
    { id: 'busquedas-sin-resultado',      label: 'Busquedas sin resultado (La Tienda)' },
    { id: 'ajustes-vigilante',            label: 'Ajustes del vigilante de expedientes' },
    // RECUPERAR-IMPORTES (2026-09-28). Pantalla de captura en tabla; escribe dinero.
    { id: 'capturar-importes',            label: 'Capturar importes de facturas' },
    { id: 'empresa',               label: 'Datos de la empresa' },
    // FLUJOS-FIJOS (Entrega 1): pantallas de workflows configurables dormidas.
    // { id: 'workflows',             label: 'Workflows' },
    // { id: 'workflows-personas',    label: 'Personas por workflow' },
    // { id: 'estados',               label: 'Estados (catalogo)' },
    // TEMPORAL — quitar en Commit B5
    { id: 'test-subir-imagen',     label: 'Test SubirImagen' },
  ]},
  { id: 'dashboard',     icono: '📊', label: 'Dashboard',      subs: [
    { id: 'general',               label: 'Vista general' },
    { id: 'operaciones',           label: 'Operaciones' },
    { id: 'comercial',             label: 'Comercial' },
    { id: 'finanzas',              label: 'Finanzas' },
  ]},
  { id: 'cotizaciones',  icono: '📝', label: 'Cotizaciones',   subs: [
    { id: 'tienda',                label: 'La Tienda' },
    { id: 'en-cotizacion',         label: 'En cotización' },
    { id: 'catalogo',              label: 'Mis Productos' },
    { id: 'listas',                label: 'Listas pactadas' },
    { id: 'colecciones',           label: 'Colecciones' },
    { id: 'mis-links',             label: 'Mis links' },
    { id: 'tipo-cambio',           label: 'Tipo de cambio' },
    { id: 'configuracion',         label: 'Configuración' },
    { id: 'syscom',                label: 'Conexión Syscom' },
    { id: 'cambiar-estado',        label: 'Cambiar estado de cotización' },
  ]},
  { id: 'aplicativos',   icono: '📱', label: 'Aplicativos',    subs: [
    { id: 'expo',                  label: 'Expo Alianza' },
    { id: 'beeptv',                label: 'BeepTV' },
  ]},
  { id: 'comercial',     icono: '💼', label: 'Comercial',      subs: [
    { id: 'clientes',              label: 'Clientes' },
    { id: 'directorio',            label: 'Directorio' },
    { id: 'cotizaciones',          label: 'Cotizaciones' },
    { id: 'prospectos',            label: 'Prospectos' },
  ]},
  { id: 'marketing',     icono: '📣', label: 'Marketing',      subs: [
    { id: 'campanas',              label: 'Campañas' },
    { id: 'calendario',            label: 'Calendario' },
    { id: 'biblioteca',            label: 'Biblioteca' },
    { id: 'recursos',              label: 'Recursos' },
    { id: 'credenciales',          label: 'Credenciales' },
    { id: 'metricas',              label: 'Métricas' },
    { id: 'agentes',               label: 'Agentes IA' },
    { id: 'expo',                  label: 'Expo Leads' },
    { id: 'kiosko',                label: 'Modo Kiosko' },
  ]},
];

function normalizarRol(rol) {
  if (!rol) return '';
  const r = String(rol).toLowerCase();
  if (r.includes('direcc')) return 'direccion';
  if (r.includes('admin')) return 'administracion';
  if (r.includes('superv')) return 'supervisor';
  if (r.includes('comerc')) return 'comercial';
  if (r.includes('market')) return 'marketing';
  if (r.includes('operac')) return 'operaciones';
  if (r.includes('técnic') || r.includes('tecnic')) return 'tecnico';
  if (r.includes('almac')) return 'almacen';
  return r;
}

function iniciales(nombre) {
  return (nombre || '?')
    .split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join('');
}

function fechaCorta(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function authHeaders() {
  const token = localStorage.getItem('beep_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Usuarios({ rolUsuario, esCelular }) {
  const rolKey = normalizarRol(rolUsuario);
  const esDireccion = rolKey === 'direccion';
  const esAdmin = esDireccion || rolKey === 'administracion';

  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [pestana, setPestana] = useState('activo'); // activo | inactivo | archivado
  const [seleccion, setSeleccion] = useState(new Set());
  const [passwordModal, setPasswordModal] = useState(null); // { usuario, password }
  const [resetModal, setResetModal] = useState(null); // { usuario } — modal para confirmar reset
  const [resetPasswordEspecifica, setResetPasswordEspecifica] = useState('');
  const [resetEnProceso, setResetEnProceso] = useState(false);
  const [toast, setToast] = useState('');
  // Panel lateral de permisos
  const [panelUsuario, setPanelUsuario] = useState(null);   // usuario completo
  const [permisosEditando, setPermisosEditando] = useState({}); // { 'mod.sub': bool }
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);
  // Modal de nuevo usuario
  const [modalNuevo, setModalNuevo] = useState(false);
  const [formNuevo, setFormNuevo] = useState({ nombre: '', correo: '', rol: 'tecnico', tecnico_externo: false });
  const [creandoUsuario, setCreandoUsuario] = useState(false);
  const [errorNuevo, setErrorNuevo] = useState('');
  // PERM-1.5: Modal de edicion de usuario existente
  const [modalEditar, setModalEditar] = useState(null); // usuario en edicion o null
  const [formEditar, setFormEditar] = useState({ nombre: '', correo: '', rol: '' });
  const [editando, setEditando] = useState(false);
  const [errorEditar, setErrorEditar] = useState('');

  // PERM-1.5: id del admin que esta operando (para PROTECCION 1: dropdown rol propio)
  const { usuario: usuarioActual } = useUsuario();

  const inputStyle = {
    width: '100%', padding: '10px 12px',
    border: `1px solid ${COLOR_GRIS_CLARO}55`, borderRadius: 8,
    fontSize: 13, fontFamily: PP, outline: 'none', boxSizing: 'border-box',
    color: COLOR_GRIS, background: '#fff',
  };

  const cargarUsuarios = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    try {
      const r = await fetch(`${API}/api/usuarios`, { headers: authHeaders() });
      const d = await r.json();
      if (d.ok && Array.isArray(d.usuarios)) {
        setUsuarios(d.usuarios);
      } else {
        setErrorCarga(d.error || 'Error cargando usuarios');
      }
    } catch (e) {
      setErrorCarga('Error de red');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (esAdmin) cargarUsuarios();
    else setCargando(false);
  }, [esAdmin, cargarUsuarios]);

  const mostrarToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const visibles = useMemo(() => {
    return usuarios.filter((u) => (u.estado || 'activo') === pestana);
  }, [usuarios, pestana]);

  // Reset selección cuando cambia de pestaña
  useEffect(() => { setSeleccion(new Set()); }, [pestana]);

  const toggleSeleccion = (id) => {
    const nueva = new Set(seleccion);
    if (nueva.has(id)) nueva.delete(id);
    else nueva.add(id);
    setSeleccion(nueva);
  };

  const seleccionarTodos = () => {
    if (seleccion.size === visibles.length) setSeleccion(new Set());
    else setSeleccion(new Set(visibles.map(u => u.id)));
  };

  const cambiarEstadoMasivo = async (estado) => {
    if (seleccion.size === 0) return;
    try {
      const r = await fetch(`${API}/api/usuarios/masivo/estado`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ ids: Array.from(seleccion), estado }),
      });
      const d = await r.json();
      if (d.ok) {
        mostrarToast(`${d.actualizados} usuario(s) actualizado(s)`);
        setSeleccion(new Set());
        cargarUsuarios();
      } else {
        mostrarToast(d.error || 'Error al actualizar');
      }
    } catch {
      mostrarToast('Error de red');
    }
  };

  const cambiarEstadoIndividual = async (id, estado) => {
    try {
      const r = await fetch(`${API}/api/usuarios/${id}/estado`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ estado }),
      });
      const d = await r.json();
      if (d.ok) {
        mostrarToast('Usuario actualizado');
        cargarUsuarios();
      } else {
        mostrarToast(d.error || 'Error al actualizar');
      }
    } catch {
      mostrarToast('Error de red');
    }
  };

  const abrirResetPassword = (usuario) => {
    if (!esDireccion) {
      mostrarToast('Solo Dirección puede resetear contraseñas');
      return;
    }
    setResetPasswordEspecifica('');
    setResetModal({ usuario });
  };

  // VER-COMO: inicia la impersonacion. iniciarVerComo respalda la sesion del
  // director, instala el token del objetivo y recarga la app completa; aqui
  // solo mostramos el error si el backend rechaza (403 direccion/inactivo, etc).
  const verComoUsuario = async (usuario) => {
    if (!esDireccion) {
      mostrarToast('Solo Dirección puede usar Ver como');
      return;
    }
    const r = await iniciarVerComo(usuario);
    if (!r.ok) mostrarToast(r.error);
  };

  const confirmarResetPassword = async () => {
    if (!resetModal) return;
    const { usuario } = resetModal;
    setResetEnProceso(true);
    try {
      if (resetPasswordEspecifica.trim()) {
        // Usar contraseña específica
        const r = await fetch(`${API}/api/usuarios/${usuario.id}/cambiar-password`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ password_nueva: resetPasswordEspecifica.trim() }),
        });
        const d = await r.json();
        if (d.ok) {
          setResetModal(null);
          setPasswordModal({ usuario, password: resetPasswordEspecifica.trim() });
        } else {
          mostrarToast(d.error || 'Error cambiando contraseña');
        }
      } else {
        // Generar temporal automática
        const r = await fetch(`${API}/api/usuarios/${usuario.id}/reset-password`, {
          method: 'PUT',
          headers: authHeaders(),
        });
        const d = await r.json();
        if (d.ok && d.password_temporal) {
          setResetModal(null);
          setPasswordModal({ usuario, password: d.password_temporal });
        } else {
          mostrarToast(d.error || 'Error generando contraseña');
        }
      }
    } catch {
      mostrarToast('Error de red');
    } finally {
      setResetEnProceso(false);
    }
  };

  // ============ CREAR USUARIO ============
  const abrirModalNuevo = () => {
    setFormNuevo({ nombre: '', correo: '', rol: 'tecnico', tecnico_externo: false });
    setErrorNuevo('');
    setModalNuevo(true);
  };

  const crearUsuario = async (e) => {
    e.preventDefault();
    setErrorNuevo('');
    if (!formNuevo.nombre.trim() || !formNuevo.correo.trim() || !formNuevo.rol) {
      setErrorNuevo('Completa todos los campos requeridos');
      return;
    }
    setCreandoUsuario(true);
    try {
      const r = await fetch(`${API}/api/usuarios/crear`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          nombre: formNuevo.nombre.trim(),
          correo: formNuevo.correo.trim().toLowerCase(),
          rol: formNuevo.rol,
          tecnico_externo: formNuevo.rol === ROLES.TECNICO ? !!formNuevo.tecnico_externo : false,
        }),
      });
      const d = await r.json();
      if (d.ok && d.password_temporal) {
        setModalNuevo(false);
        // Reusar el modal de credenciales
        setPasswordModal({ usuario: d.usuario, password: d.password_temporal });
        cargarUsuarios();
      } else {
        setErrorNuevo(d.error || 'Error creando usuario');
      }
    } catch {
      setErrorNuevo('Error de red');
    } finally {
      setCreandoUsuario(false);
    }
  };

  // ============ EDITAR USUARIO (PERM-1.5) ============
  const abrirModalEditar = (u) => {
    setFormEditar({
      nombre: u.nombre || '',
      correo: u.correo || '',
      rol: u.rol || 'tecnico',
      tecnico_externo: u.tecnico_externo === true,
    });
    setErrorEditar('');
    setModalEditar(u);
  };

  const guardarEdicion = async (e) => {
    e.preventDefault();
    setErrorEditar('');
    const nombre = formEditar.nombre.trim();
    const correo = formEditar.correo.trim().toLowerCase();
    if (!nombre) { setErrorEditar('El nombre no puede estar vacío'); return; }
    if (!correo || !correo.includes('@') || !correo.split('@')[1]?.includes('.')) {
      setErrorEditar('Correo con formato inválido'); return;
    }
    // PATCH: solo enviar campos que cambiaron
    const body = {};
    if (nombre !== modalEditar.nombre) body.nombre = nombre;
    if (correo !== (modalEditar.correo || '').toLowerCase()) body.correo = correo;
    if (formEditar.rol !== modalEditar.rol) body.rol = formEditar.rol;
    // CALENDARIO-E1: flag interno/externo (solo aplica a tecnico).
    const externoFinal = formEditar.rol === ROLES.TECNICO ? !!formEditar.tecnico_externo : false;
    if (externoFinal !== (modalEditar.tecnico_externo === true)) body.tecnico_externo = externoFinal;
    if (Object.keys(body).length === 0) { setModalEditar(null); return; }

    setEditando(true);
    try {
      const r = await fetch(`${API}/api/usuarios/${modalEditar.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.ok && d.usuario) {
        setUsuarios(prev => prev.map(u => u.id === d.usuario.id ? { ...u, ...d.usuario } : u));
        setModalEditar(null);
        mostrarToast('Usuario actualizado');
      } else {
        setErrorEditar(d.error || 'Error guardando');
      }
    } catch {
      setErrorEditar('Error de red, intenta de nuevo');
    } finally {
      setEditando(false);
    }
  };

  // ============ PANEL DE PERMISOS ============
  const abrirPanelPermisos = (usuario) => {
    setPanelUsuario(usuario);
    setPermisosEditando(usuario.permisos || {});
  };

  const cerrarPanelPermisos = () => {
    setPanelUsuario(null);
    setPermisosEditando({});
  };

  const togglePermisoSub = (modId, subId) => {
    const k = `${modId}.${subId}`;
    setPermisosEditando(prev => ({ ...prev, [k]: !prev[k] }));
  };

  const togglePermisoModulo = (modulo) => {
    // Si todos los subs están activos → desactivar todos. Si no → activar todos.
    const todosActivos = modulo.subs.every(s => permisosEditando[`${modulo.id}.${s.id}`]);
    const nuevo = { ...permisosEditando };
    modulo.subs.forEach(s => { nuevo[`${modulo.id}.${s.id}`] = !todosActivos; });
    setPermisosEditando(nuevo);
  };

  const guardarPermisos = async () => {
    if (!panelUsuario) return;
    setGuardandoPermisos(true);
    try {
      const r = await fetch(`${API}/api/usuarios/${panelUsuario.id}/permisos`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ permisos: permisosEditando }),
      });
      const d = await r.json();
      if (d.ok) {
        mostrarToast('Permisos guardados');
        // Actualizar la lista local sin recargar
        setUsuarios(prev => prev.map(u => u.id === panelUsuario.id ? { ...u, permisos: permisosEditando } : u));
        // Si el usuario edita sus propios permisos, refrescar localStorage
        try {
          const me = JSON.parse(localStorage.getItem('beep_usuario') || '{}');
          if (me && String(me.id) === String(panelUsuario.id)) {
            localStorage.setItem('beep_permisos', JSON.stringify(permisosEditando));
          }
        } catch {}
        cerrarPanelPermisos();
      } else {
        mostrarToast(d.error || 'Error guardando permisos');
      }
    } catch {
      mostrarToast('Error de red');
    } finally {
      setGuardandoPermisos(false);
    }
  };

  const copiarCredenciales = async () => {
    if (!passwordModal) return;
    const { usuario, password } = passwordModal;
    const texto = `Hola ${usuario.nombre}, tus nuevas credenciales de Beep OS son:\n\nUsuario: ${usuario.correo}\nContraseña temporal: ${password}\n\nEntra en: app.beepmexico.com\nAl entrar te pedirá cambiarla.`;
    try {
      await navigator.clipboard.writeText(texto);
      mostrarToast('✓ Copiado al portapapeles');
    } catch {
      // Fallback para navegadores viejos
      const ta = document.createElement('textarea');
      ta.value = texto;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); mostrarToast('✓ Copiado'); }
      catch { mostrarToast('No se pudo copiar'); }
      document.body.removeChild(ta);
    }
  };

  // ============================================
  // ACCESO DENEGADO
  // ============================================
  if (!esAdmin) {
    return (
      <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_GRIS }}>Acceso restringido</div>
          <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
            Este módulo es exclusivo para Dirección y Administración.
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // UI
  // ============================================
  const pestanas = [
    { id: 'activo',     label: 'Activos',     color: COLOR_VERDE },
    { id: 'inactivo',   label: 'Inactivos',   color: COLOR_AMARILLO },
    { id: 'archivado',  label: 'Archivados',  color: COLOR_GRIS_CLARO },
  ];

  const conteoPorEstado = {
    activo:    usuarios.filter(u => (u.estado || 'activo') === 'activo').length,
    inactivo:  usuarios.filter(u => u.estado === 'inactivo').length,
    archivado: usuarios.filter(u => u.estado === 'archivado').length,
  };

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', padding: esCelular ? 12 : 20 }}>
      <style>{`
        @keyframes fadeInPwd { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: COLOR_OSCURO, color: '#fff', padding: '10px 20px',
          borderRadius: 20, fontSize: 13, fontWeight: 600, zIndex: 300,
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)', fontFamily: PP,
        }}>{toast}</div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>Usuarios</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
            Administra accesos internos de Beep México
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={cargarUsuarios}
            style={{
              background: 'transparent', border: `1px solid ${COLOR_GRIS_CLARO}55`,
              borderRadius: 10, padding: '8px 14px', color: COLOR_GRIS,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
            }}
          >↻ Refrescar</button>
          <button
            onClick={abrirModalNuevo}
            style={{
              background: COLOR_OSCURO, border: 'none',
              borderRadius: 10, padding: '8px 16px', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
            }}
          >+ Nuevo usuario</button>
        </div>
      </div>

      {/* SUB-PESTAÑAS */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {pestanas.map(p => {
          const activa = pestana === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPestana(p.id)}
              style={{
                padding: '8px 16px', borderRadius: 999, border: 'none',
                background: activa ? p.color : '#fff',
                color: activa ? '#fff' : COLOR_GRIS,
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                boxShadow: activa ? `0 2px 8px ${p.color}44` : '0 1px 2px rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {p.label}
              <span style={{
                background: activa ? 'rgba(255,255,255,0.25)' : COLOR_FONDO,
                color: activa ? '#fff' : COLOR_GRIS_CLARO,
                borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700,
              }}>{conteoPorEstado[p.id]}</span>
            </button>
          );
        })}
      </div>

      {/* BARRA DE ACCIONES MASIVAS */}
      {seleccion.size > 0 && (
        <div style={{
          background: COLOR_OSCURO, color: '#fff', borderRadius: 12,
          padding: '12px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 10,
          boxShadow: '0 4px 16px rgba(47,95,158,0.3)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            ✓ {seleccion.size} seleccionado{seleccion.size !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pestana !== 'activo' && (
              <button
                onClick={() => cambiarEstadoMasivo('activo')}
                style={botonMasivoStyle(COLOR_VERDE)}
              ><Play size={15} strokeWidth={2} /> Activar seleccionados</button>
            )}
            {pestana !== 'inactivo' && pestana !== 'archivado' && (
              <button
                onClick={() => cambiarEstadoMasivo('inactivo')}
                style={botonMasivoStyle(COLOR_AMARILLO, COLOR_GRIS)}
              ><Pause size={15} strokeWidth={2} /> Desactivar seleccionados</button>
            )}
            {pestana !== 'archivado' && (
              <button
                onClick={() => cambiarEstadoMasivo('archivado')}
                style={botonMasivoStyle('rgba(255,255,255,0.15)')}
              ><Archive size={15} strokeWidth={2} /> Archivar seleccionados</button>
            )}
            <button
              onClick={() => setSeleccion(new Set())}
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 8, padding: '6px 12px', color: '#fff',
                fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
              }}
            >Cancelar</button>
          </div>
        </div>
      )}

      {/* LISTA DE USUARIOS */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        {cargando ? (
          <div style={{ padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            Cargando usuarios…
          </div>
        ) : errorCarga ? (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ color: COLOR_ROJO, fontSize: 13, marginBottom: 8 }}>{errorCarga}</div>
            <button onClick={cargarUsuarios} style={{
              background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP,
            }}>Reintentar</button>
          </div>
        ) : visibles.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            No hay usuarios {pestanas.find(p => p.id === pestana)?.label.toLowerCase()}
          </div>
        ) : (
          <>
            {/* Header con checkbox "seleccionar todos" */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderBottom: '1px solid #eee',
              fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              <input
                type="checkbox"
                checked={seleccion.size === visibles.length && visibles.length > 0}
                onChange={seleccionarTodos}
                style={{ cursor: 'pointer', accentColor: COLOR_PRINCIPAL }}
              />
              <span>Seleccionar todos ({visibles.length})</span>
            </div>

            {/* Filas */}
            {visibles.map(u => {
              const rolNorm = normalizarRol(u.rol);
              // PERM-1: label desde catalogo unico, color desde paleta visual local
              const rolCfg = {
                label: ROLES_LABELS[rolNorm] || u.rol,
                color: ROLES_VISUAL[rolNorm]?.color || COLOR_GRIS_CLARO,
              };
              const seleccionado = seleccion.has(u.id);
              return (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px', borderBottom: '1px solid #f5f5f5',
                  background: panelUsuario?.id === u.id ? '#e8f0ff' : (seleccionado ? '#f0f7ff' : 'transparent'),
                  transition: 'background 0.15s',
                  // Wrap SIEMPRE (no solo en celular): en tablet 768px la fila de un
                  // director no cabe en una linea (chips + menu) y el ancestro recorta
                  // con overflow:hidden. Con wrap, el bloque de acciones baja a una 2a
                  // linea en vez de recortarse (veto Doña Frida). AccionesUsuario lleva
                  // marginLeft:auto para quedar a la derecha tambien al envolver.
                  flexWrap: 'wrap',
                }}>
                  <input
                    type="checkbox"
                    checked={seleccionado}
                    onChange={(e) => { e.stopPropagation(); toggleSeleccion(u.id); }}
                    style={{ cursor: 'pointer', accentColor: COLOR_PRINCIPAL, flexShrink: 0 }}
                  />
                  {/* Avatar (clickeable → abre panel de permisos) */}
                  <div
                    onClick={() => abrirPanelPermisos(u)}
                    title="Ver permisos"
                    style={{
                      width: 40, height: 40, borderRadius: '50%',
                      background: rolCfg.color, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, flexShrink: 0, cursor: 'pointer',
                    }}>{iniciales(u.nombre)}</div>

                  {/* Info (clickeable → abre panel de permisos) */}
                  <div onClick={() => abrirPanelPermisos(u)} style={{ flex: 1, minWidth: 140, cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GRIS }}>
                      {u.nombre}
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                      {u.correo}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{
                        background: `${rolCfg.color}22`, color: rolCfg.color,
                        padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                      }}>{rolCfg.label}</span>
                      {u.debe_cambiar_password && (
                        <span style={{
                          background: '#fff3cd', color: '#856404',
                          padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                        }}>🔑 Debe cambiar password</span>
                      )}
                    </div>
                  </div>

                  {/* Último acceso */}
                  {!esCelular && (
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, minWidth: 110, textAlign: 'right' }}>
                      <div style={{ fontWeight: 600 }}>Último acceso</div>
                      <div>{fechaCorta(u.ultimo_acceso)}</div>
                    </div>
                  )}

                  {/* Acciones individuales — chips legibles + menu contextual */}
                  <AccionesUsuario
                    u={u}
                    esDireccion={esDireccion}
                    esAdmin={esAdmin}
                    pestana={pestana}
                    esFilaDireccion={rolNorm === 'direccion'}
                    onPermisos={() => abrirPanelPermisos(u)}
                    onVerComo={() => verComoUsuario(u)}
                    onEditar={() => abrirModalEditar(u)}
                    onReset={() => abrirResetPassword(u)}
                    onArchivar={() => cambiarEstadoIndividual(u.id, 'archivado')}
                    onDesactivar={() => cambiarEstadoIndividual(u.id, 'inactivo')}
                    onActivar={() => cambiarEstadoIndividual(u.id, 'activo')}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* MODAL DE CONTRASEÑA TEMPORAL */}
      {passwordModal && (
        <>
          <div
            onClick={() => setPasswordModal(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#fff', borderRadius: 16, padding: 24,
            width: '90%', maxWidth: 440, zIndex: 401,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            animation: 'fadeInPwd 0.25s ease', fontFamily: PP,
          }}>
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🔑</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_GRIS, textAlign: 'center', marginBottom: 6 }}>
              Contraseña temporal generada
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, textAlign: 'center', marginBottom: 16 }}>
              Para <strong>{passwordModal.usuario.nombre}</strong>
            </div>

            <div style={{
              background: COLOR_FONDO, borderRadius: 10, padding: 12,
              marginBottom: 14, fontFamily: 'monospace',
            }}>
              <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginBottom: 4, fontFamily: PP, fontWeight: 700, textTransform: 'uppercase' }}>Usuario</div>
              <div style={{ fontSize: 13, color: COLOR_GRIS, marginBottom: 10 }}>{passwordModal.usuario.correo}</div>
              <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginBottom: 4, fontFamily: PP, fontWeight: 700, textTransform: 'uppercase' }}>Contraseña temporal</div>
              <div style={{
                fontSize: 16, color: COLOR_OSCURO, fontWeight: 700,
                userSelect: 'all', letterSpacing: 1,
              }}>{passwordModal.password}</div>
            </div>

            <div style={{
              background: '#fff9db', border: '1px solid #ffe58f',
              borderRadius: 8, padding: '8px 12px', marginBottom: 14,
              fontSize: 11, color: '#8a6d00', lineHeight: 1.5,
            }}>
              ⚠️ El usuario deberá cambiar esta contraseña al entrar. Compártela solo por un canal seguro.
            </div>

            <button
              onClick={copiarCredenciales}
              style={{
                width: '100%', background: COLOR_OSCURO, color: '#fff',
                border: 'none', borderRadius: 10, padding: '12px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                fontFamily: PP, marginBottom: 8,
              }}
            >📋 Copiar y compartir</button>

            <button
              onClick={() => setPasswordModal(null)}
              style={{
                width: '100%', background: 'transparent', color: COLOR_GRIS_CLARO,
                border: `1px solid ${COLOR_GRIS_CLARO}44`, borderRadius: 10, padding: '10px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
              }}
            >Cerrar</button>
          </div>
        </>
      )}

      {/* MODAL CONFIRMAR RESET PASSWORD */}
      {resetModal && (
        <>
          <div onClick={() => !resetEnProceso && setResetModal(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#fff', borderRadius: 16, padding: 24,
            width: '90%', maxWidth: 440, zIndex: 401,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            animation: 'fadeInPwd 0.25s ease', fontFamily: PP,
          }}>
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🔑</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_GRIS, textAlign: 'center', marginBottom: 6 }}>
              Resetear contraseña
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, textAlign: 'center', marginBottom: 18 }}>
              Para <strong>{resetModal.usuario.nombre}</strong> ({resetModal.usuario.correo})
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                Contraseña específica (opcional)
              </label>
              <input
                type="text"
                value={resetPasswordEspecifica}
                onChange={e => setResetPasswordEspecifica(e.target.value)}
                placeholder="Dejar vacío para generar temporal"
                disabled={resetEnProceso}
                style={inputStyle}
              />
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                Si se deja vacío, se generará una contraseña temporal automáticamente.
              </div>
            </div>

            <button
              onClick={confirmarResetPassword}
              disabled={resetEnProceso}
              style={{
                width: '100%', background: COLOR_OSCURO, color: '#fff',
                border: 'none', borderRadius: 10, padding: '12px',
                fontSize: 13, fontWeight: 700, cursor: resetEnProceso ? 'wait' : 'pointer',
                fontFamily: PP, marginBottom: 8, opacity: resetEnProceso ? 0.6 : 1,
              }}
            >{resetEnProceso ? 'Procesando...' : 'Confirmar'}</button>

            <button
              onClick={() => setResetModal(null)}
              disabled={resetEnProceso}
              style={{
                width: '100%', background: 'transparent', color: COLOR_GRIS_CLARO,
                border: `1px solid ${COLOR_GRIS_CLARO}44`, borderRadius: 10, padding: '10px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
              }}
            >Cancelar</button>
          </div>
        </>
      )}

      {/* MODAL NUEVO USUARIO */}
      {modalNuevo && (
        <>
          <div onClick={() => !creandoUsuario && setModalNuevo(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400,
          }} />
          <form onSubmit={crearUsuario} style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#fff', borderRadius: 16, padding: 24,
            width: '90%', maxWidth: 440, zIndex: 401,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            animation: 'fadeInPwd 0.25s ease', fontFamily: PP,
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_GRIS, marginBottom: 4 }}>
              + Nuevo usuario
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 18 }}>
              Se generará una contraseña temporal automáticamente
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                Nombre completo *
              </label>
              <input
                value={formNuevo.nombre}
                onChange={e => setFormNuevo({ ...formNuevo, nombre: e.target.value })}
                placeholder="Ej: Juan Pérez"
                required
                disabled={creandoUsuario}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                Correo *
              </label>
              <input
                type="email"
                value={formNuevo.correo}
                onChange={e => setFormNuevo({ ...formNuevo, correo: e.target.value })}
                placeholder="usuario@beepmx.com"
                required
                disabled={creandoUsuario}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                Rol *
              </label>
              <select
                value={formNuevo.rol}
                onChange={e => setFormNuevo({ ...formNuevo, rol: e.target.value })}
                disabled={creandoUsuario}
                style={inputStyle}
              >
                {/* PERM-1: opciones del catalogo unico de roles */}
                {ROLES_CREABLES.map(r => (
                  <option key={r} value={r}>{ROLES_LABELS[r]}</option>
                ))}
              </select>
            </div>

            {/* CALENDARIO-E1: tecnico interno vs externo (solo el flag). */}
            {formNuevo.rol === ROLES.TECNICO && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, minHeight: 44, cursor: 'pointer', fontSize: 13, color: COLOR_GRIS }}>
                <input type="checkbox" checked={!!formNuevo.tecnico_externo}
                  onChange={e => setFormNuevo({ ...formNuevo, tecnico_externo: e.target.checked })}
                  disabled={creandoUsuario} style={{ width: 20, height: 20, flexShrink: 0 }} />
                Tecnico EXTERNO (ocasional). Deja sin marcar si es tecnico interno.
              </label>
            )}

            {errorNuevo && (
              <div style={{
                background: '#fee2e2', color: COLOR_ROJO, fontSize: 12,
                padding: '8px 12px', borderRadius: 8, marginBottom: 12,
              }}>⚠️ {errorNuevo}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setModalNuevo(false)}
                disabled={creandoUsuario}
                style={{
                  flex: 1, background: 'transparent', border: `1px solid ${COLOR_GRIS_CLARO}66`,
                  borderRadius: 10, padding: '12px', color: COLOR_GRIS_CLARO,
                  fontSize: 12, fontWeight: 600, cursor: creandoUsuario ? 'not-allowed' : 'pointer',
                  fontFamily: PP,
                }}
              >Cancelar</button>
              <button
                type="submit"
                disabled={creandoUsuario || !formNuevo.nombre.trim() || !formNuevo.correo.trim()}
                style={{
                  flex: 2, background: creandoUsuario ? '#a0a0a0' : COLOR_OSCURO, color: '#fff',
                  border: 'none', borderRadius: 10, padding: '12px',
                  fontSize: 13, fontWeight: 700,
                  cursor: creandoUsuario ? 'not-allowed' : 'pointer',
                  fontFamily: PP,
                }}
              >{creandoUsuario ? 'Creando…' : 'Crear usuario'}</button>
            </div>
          </form>
        </>
      )}

      {/* PERM-1.5: MODAL DE EDICION DE USUARIO */}
      {modalEditar && (() => {
        const esPropio = usuarioActual && Number(usuarioActual.id) === Number(modalEditar.id);
        return (
          <>
            <div onClick={() => !editando && setModalEditar(null)} style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400,
            }} />
            <form onSubmit={guardarEdicion} style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              background: '#fff', borderRadius: 16, padding: 24,
              width: '90%', maxWidth: 440, zIndex: 401,
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              animation: 'fadeInPwd 0.25s ease', fontFamily: PP,
              maxHeight: 'calc(var(--beep-vh) * 90)', overflowY: 'auto',
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_GRIS, marginBottom: 4 }}>
                Editar usuario
              </div>
              <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 18 }}>
                Modifica nombre, correo o rol. La contraseña se cambia con el botón 🔑.
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                  Nombre completo *
                </label>
                <input
                  value={formEditar.nombre}
                  onChange={e => setFormEditar({ ...formEditar, nombre: e.target.value })}
                  required
                  disabled={editando}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                  Correo *
                </label>
                <input
                  type="email"
                  value={formEditar.correo}
                  onChange={e => setFormEditar({ ...formEditar, correo: e.target.value })}
                  required
                  disabled={editando}
                  style={inputStyle}
                />
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                  ⚠️ Cambiar el correo afectará el login del usuario
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>
                  Rol *
                </label>
                <select
                  value={formEditar.rol}
                  onChange={e => setFormEditar({ ...formEditar, rol: e.target.value })}
                  disabled={editando || esPropio}
                  style={{
                    ...inputStyle,
                    background: esPropio ? '#f3f4f4' : '#fff',
                    cursor: esPropio ? 'not-allowed' : 'pointer',
                  }}
                >
                  {ROLES_LISTA.map(r => (
                    <option key={r} value={r}>{ROLES_LABELS[r]}</option>
                  ))}
                </select>
                {esPropio && (
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                    🔒 No puedes cambiar tu propio rol por seguridad
                  </div>
                )}
                {/* CALENDARIO-E1: tecnico interno vs externo. */}
                {formEditar.rol === ROLES.TECNICO && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, minHeight: 44, cursor: 'pointer', fontSize: 13, color: COLOR_GRIS }}>
                    <input type="checkbox" checked={!!formEditar.tecnico_externo}
                      onChange={e => setFormEditar({ ...formEditar, tecnico_externo: e.target.checked })}
                      disabled={editando} style={{ width: 20, height: 20, flexShrink: 0 }} />
                    Tecnico EXTERNO (ocasional)
                  </label>
                )}
              </div>

              {errorEditar && (
                <div style={{
                  background: '#fee2e2', color: COLOR_ROJO, fontSize: 12,
                  padding: '8px 12px', borderRadius: 8, marginBottom: 12,
                }}>⚠️ {errorEditar}</div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setModalEditar(null)}
                  disabled={editando}
                  style={{
                    flex: 1, background: 'transparent', border: `1px solid ${COLOR_GRIS_CLARO}66`,
                    borderRadius: 10, padding: '12px', color: COLOR_GRIS_CLARO,
                    fontSize: 12, fontWeight: 600, cursor: editando ? 'not-allowed' : 'pointer',
                    fontFamily: PP,
                  }}
                >Cancelar</button>
                <button
                  type="submit"
                  disabled={editando || !formEditar.nombre.trim() || !formEditar.correo.trim()}
                  style={{
                    flex: 2, background: editando ? '#a0a0a0' : COLOR_OSCURO, color: '#fff',
                    border: 'none', borderRadius: 10, padding: '12px',
                    fontSize: 13, fontWeight: 700,
                    cursor: editando ? 'not-allowed' : 'pointer',
                    fontFamily: PP,
                  }}
                >{editando ? 'Guardando…' : 'Guardar cambios'}</button>
              </div>
            </form>
          </>
        );
      })()}

      {/* PANEL LATERAL DE PERMISOS */}
      {panelUsuario && (
        <>
          <div
            onClick={cerrarPanelPermisos}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 350 }}
          />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: esCelular ? '100%' : 480, maxWidth: '100%',
            background: '#fff', zIndex: 351,
            display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
            animation: 'slideInPanel 0.25s ease',
            fontFamily: PP,
          }}>
            <style>{`
              @keyframes slideInPanel { from { transform: translateX(100%); } to { transform: translateX(0); } }
            `}</style>

            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #eee',
              display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: ROLES_VISUAL[normalizarRol(panelUsuario.rol)]?.color || COLOR_GRIS_CLARO,
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, flexShrink: 0,
              }}>{iniciales(panelUsuario.nombre)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Permisos de
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_GRIS, lineHeight: 1.2 }}>
                  {panelUsuario.nombre}
                </div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{panelUsuario.correo}</div>
              </div>
              <button
                onClick={cerrarPanelPermisos}
                style={{
                  background: COLOR_FONDO, border: 'none', borderRadius: '50%',
                  width: 32, height: 32, cursor: 'pointer', fontSize: 16,
                  color: COLOR_GRIS, fontFamily: PP,
                }}
              >×</button>
            </div>

            {/* Lista de módulos */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {/* AREAS-EN-USUARIOS: va ARRIBA de los permisos a proposito. Los permisos
                  dicen que PUEDE VER una persona; las areas dicen que TRABAJO LE CAE.
                  Son dos cosas distintas y la segunda no tenia donde tocarse. */}
              <AreasDelUsuario
                usuarioId={panelUsuario.id}
                puedeEditar={normalizarRol(rolUsuario) === 'direccion' || normalizarRol(rolUsuario) === 'administracion'}
              />
              {MODULOS.map(mod => {
                const subsActivos = mod.subs.filter(s => permisosEditando[`${mod.id}.${s.id}`]).length;
                const todosActivos = subsActivos === mod.subs.length;
                const algunosActivos = subsActivos > 0 && !todosActivos;
                return (
                  <div key={mod.id} style={{
                    background: COLOR_FONDO, borderRadius: 12,
                    padding: 12, marginBottom: 10,
                  }}>
                    {/* Header del módulo con checkbox */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      cursor: 'pointer', marginBottom: 8,
                    }}>
                      <input
                        type="checkbox"
                        checked={todosActivos}
                        ref={el => { if (el) el.indeterminate = algunosActivos; }}
                        onChange={() => togglePermisoModulo(mod)}
                        style={{ cursor: 'pointer', accentColor: COLOR_OSCURO, width: 16, height: 16 }}
                      />
                      <span style={{ fontSize: 16 }}>{mod.icono}</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: COLOR_GRIS, flex: 1 }}>
                        {mod.label}
                      </span>
                      <span style={{
                        fontSize: 10, color: COLOR_GRIS_CLARO, fontWeight: 700,
                        background: '#fff', padding: '2px 8px', borderRadius: 999,
                      }}>{subsActivos}/{mod.subs.length}</span>
                    </label>

                    {/* Sub-módulos */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr',
                      gap: 6, paddingLeft: 24,
                    }}>
                      {mod.subs.map(sub => {
                        const k = `${mod.id}.${sub.id}`;
                        return (
                          <label key={sub.id} style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            cursor: 'pointer', fontSize: 12, color: COLOR_GRIS,
                            padding: '4px 0',
                          }}>
                            <input
                              type="checkbox"
                              checked={!!permisosEditando[k]}
                              onChange={() => togglePermisoSub(mod.id, sub.id)}
                              style={{ cursor: 'pointer', accentColor: COLOR_PRINCIPAL }}
                            />
                            <span>{sub.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer con guardar */}
            <div style={{
              padding: '14px 16px', borderTop: '1px solid #eee',
              display: 'flex', gap: 8, flexShrink: 0,
            }}>
              <button
                onClick={cerrarPanelPermisos}
                style={{
                  background: 'transparent', border: `1px solid ${COLOR_GRIS_CLARO}66`,
                  borderRadius: 10, padding: '12px 16px', color: COLOR_GRIS,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
                }}
              >Cancelar</button>
              <button
                onClick={guardarPermisos}
                disabled={guardandoPermisos}
                style={{
                  flex: 1, background: guardandoPermisos ? '#a0a0a0' : COLOR_OSCURO, color: '#fff',
                  border: 'none', borderRadius: 10, padding: '12px',
                  fontSize: 13, fontWeight: 700, cursor: guardandoPermisos ? 'not-allowed' : 'pointer',
                  fontFamily: PP,
                }}
              >{guardandoPermisos ? 'Guardando…' : '💾 Guardar permisos'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Estilos compartidos para botones
function botonMasivoStyle(bg, color = '#fff') {
  return {
    background: bg, color: color,
    border: 'none', borderRadius: 8, padding: '8px 12px',
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    fontFamily: PP, minHeight: 40,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };
}

// ============================================
// UX-USUARIOS (2026-07-04): acciones legibles por fila
// ============================================
// Antes: 5 emojis crudos sin etiqueta (ojo/lapiz/llave/caja/pausa). Ahora:
// 2 chips visibles con icono + TEXTO (Permisos, Ver como) + un menu "⋮" con el
// resto (Editar, Restablecer contraseña, Pausar/Activar, Archivar en rojo al
// final). Presentacion pura: los onClick y las condiciones de visibilidad son
// IDENTICAS a las de antes (misma logica/endpoints).

// Chip visible (icono Lucide + texto). Deshabilitado muestra motivo via title.
// Feedback: hover (escritorio) + pressed (tactil). minHeight 44 = tactil-safe.
function ChipAccion({ Icono, texto, onClick, bg, color, borde, disabled, motivo }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? motivo : texto}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.filter = 'brightness(0.94)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.opacity = '1'; }}
      onPointerDown={(e) => { if (!disabled) e.currentTarget.style.opacity = '0.7'; }}
      onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: disabled ? '#f3f4f4' : bg,
        color: disabled ? '#b0b4b8' : color,
        border: `1.5px solid ${disabled ? '#e3e5e7' : borde}`,
        borderRadius: 8, padding: '0 14px', minHeight: 44,
        fontSize: 13, fontWeight: 600, fontFamily: PP,
        cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
        whiteSpace: 'nowrap', transition: 'opacity 0.15s, filter 0.15s',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Icono size={16} strokeWidth={2} />
      {texto}
    </button>
  );
}

// Menu contextual "⋮" con posicion fija calculada (evita recortes del scrollport).
// Cierra con: clic-fuera (listener document, permite abrir OTRO menu en un solo
// tap), Escape, scroll o resize (el menu fixed no debe quedar flotando lejos del
// boton). Items: { Icono, label, onClick, color, sep }.
function MenuContextual({ items }) {
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    const onKey = (e) => { if (e.key === 'Escape') cerrar(); };
    // Clic-fuera via document (sin backdrop): un tap en otro boton "⋮" cierra
    // este Y abre el otro en el mismo gesto (el backdrop lo habria bloqueado).
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      cerrar();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    // Scroll en cualquier ancestro (capture) o resize: el menu fixed quedaria
    // desprendido del boton -> lo cerramos (veto Don Porfirio / Don Benito).
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  if (!items.length) return null;

  const toggle = () => {
    if (!abierto && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const menuW = 240;
      const menuH = items.length * 48 + 10;
      // Abre hacia arriba si abajo no cabe pero arriba si.
      const abreArriba = (r.bottom + menuH > window.innerHeight) && (r.top > menuH);
      setCoords({
        top: abreArriba ? Math.max(8, r.top - menuH) : r.bottom + 6,
        left: Math.max(8, Math.min(r.right - menuW, window.innerWidth - menuW - 8)),
        width: menuW,
      });
    }
    setAbierto(v => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title="Más acciones"
        aria-label="Más acciones"
        onMouseEnter={(e) => { if (!abierto) e.currentTarget.style.background = '#f3f6fa'; }}
        onMouseLeave={(e) => { if (!abierto) e.currentTarget.style.background = '#fff'; }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: abierto ? '#eef2f7' : '#fff',
          color: COLOR_GRIS, border: `1.5px solid ${abierto ? COLOR_PRINCIPAL : '#e3e5e7'}`,
          borderRadius: 8, width: 44, minHeight: 44, cursor: 'pointer',
          flexShrink: 0, transition: 'background 0.15s',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <MoreVertical size={18} strokeWidth={2} />
      </button>
      {abierto && coords && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: coords.top, left: coords.left, width: coords.width,
            background: '#fff', borderRadius: 12, zIndex: 3001,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)', border: '1px solid #eceef0',
            padding: 5, fontFamily: PP,
          }}
        >
          {items.map((it, i) => (
            <React.Fragment key={i}>
              {it.sep && <div style={{ height: 1, background: '#eceef0', margin: '5px 6px' }} />}
              <button
                type="button"
                onClick={() => { setAbierto(false); it.onClick(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = it.color ? '#fdeeee' : '#f3f6fa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onPointerDown={(e) => { e.currentTarget.style.background = it.color ? '#fbdada' : '#e8eef6'; }}
                onPointerUp={(e) => { e.currentTarget.style.background = 'transparent'; }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  background: 'transparent', border: 'none', borderRadius: 8,
                  padding: '0 12px', minHeight: 44, cursor: 'pointer',
                  fontSize: 14, fontWeight: 500, fontFamily: PP,
                  color: it.color || COLOR_GRIS, textAlign: 'left',
                  transition: 'background 0.15s', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <it.Icono size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                {it.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

// Orquesta chips visibles + menu, con el MISMO orden en todas las filas.
function AccionesUsuario({
  u, esDireccion, esAdmin, pestana, esFilaDireccion,
  onPermisos, onVerComo, onEditar, onReset, onArchivar, onDesactivar, onActivar,
}) {
  // Motivo de deshabilitado para "Ver como" (consistencia: no ocultar a medias).
  let verComoMotivo = null;
  if (esFilaDireccion) verComoMotivo = 'No disponible para usuarios de dirección';
  else if (pestana !== 'activo') verComoMotivo = 'Solo disponible en usuarios activos';

  // Items del menu "⋮" — mismo orden siempre; destructiva (Archivar) al final en rojo.
  const items = [];
  if (esAdmin) items.push({ Icono: Pencil, label: 'Editar datos', onClick: onEditar });
  if (esDireccion) items.push({ Icono: KeyRound, label: 'Restablecer contraseña', onClick: onReset });
  if (pestana === 'activo') items.push({ Icono: Pause, label: 'Pausar acceso', onClick: onDesactivar });
  if (pestana === 'inactivo' || pestana === 'archivado') items.push({ Icono: Play, label: 'Activar', onClick: onActivar });
  if (pestana === 'activo' || pestana === 'inactivo') {
    items.push({ Icono: Archive, label: 'Archivar', onClick: onArchivar, color: COLOR_ROJO, sep: true });
  }

  return (
    // marginLeft:auto -> el bloque queda a la DERECHA en la 1a linea y tambien
    // cuando la fila envuelve y baja a su propia linea (veto Don Porfirio M5).
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0, alignItems: 'center', justifyContent: 'flex-end', marginLeft: 'auto' }}>
      <ChipAccion
        Icono={Shield} texto="Permisos" onClick={onPermisos}
        bg="#fff" color={COLOR_OSCURO} borde={`${COLOR_PRINCIPAL}66`}
      />
      {/* Ver como: solo para direccion (igual que hoy). Cuando no aplica al
          objetivo, se muestra deshabilitado con el motivo, no oculto a medias. */}
      {esDireccion && (
        <ChipAccion
          Icono={Eye} texto="Ver como" onClick={onVerComo}
          bg="#fff8e6" color="#8a6d00" borde="#f59e0b88"
          disabled={!!verComoMotivo} motivo={verComoMotivo}
        />
      )}
      <MenuContextual items={items} />
    </div>
  );
}

export default Usuarios;
